/**
 * MaterialsStep — Step 4: Stepper + PhaseAccumulator for material costs.
 * Items are size-scaled via getScaledWizardMaterials(medium, area).
 * PhaseAccumulator supports multi-tap quantities (×1, ×2, ×3…).
 * For paper-based mediums, also shows framing estimate + "I frame my own work" toggle.
 */

import { useMemo } from 'react';
import { T } from '@/styles/tokens';
import type { MediumKey } from '@/types/medium';
import { MEDIUMS } from '@/types/medium';
import { getScaledWizardMaterials } from '@/constants/materials';
import { StepperControl } from './StepperControl';
import { PhaseAccumulator } from './PhaseAccumulator';
import { StepNextButton } from './StepNextButton';
import { SectionIcon } from './SectionIcons';

const cardStyle: React.CSSProperties = {
  background: T.white,
  borderRadius: 16,
  border: `1px solid ${T.cream200}`,
  padding: 24,
  boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
};

interface MaterialsStepProps {
  materialsCents: number;
  medium: MediumKey;
  area: number;
  matQuantities: Map<number, number>;
  onChangeMaterials: (cents: number) => void;
  onIncrementMat: (idx: number, costDollars: number) => void;
  onDecrementMat: (idx: number, costDollars: number) => void;
  onClearMat: (idx: number, totalDollars: number) => void;
  onResetMats: () => void;
  onNext: () => void;
  framingCents?: number;
  framingWaived?: boolean;
  onToggleFramingWaived?: (waived: boolean) => void;
}

export function MaterialsStep({
  materialsCents, medium, area, matQuantities, onChangeMaterials,
  onIncrementMat, onDecrementMat, onClearMat, onResetMats, onNext,
  framingCents = 0, framingWaived = false, onToggleFramingWaived,
}: MaterialsStepProps) {
  const matItems = useMemo(
    () => getScaledWizardMaterials(medium, area).map((m) => ({ label: m.label, value: m.cost })),
    [medium, area],
  );
  const isPaperBased = MEDIUMS[medium]?.isPaperBased ?? false;
  const totalCents = materialsCents + framingCents;

  return (
    <div>
      <SectionIcon icon="materials" label="Materials" />
      <div style={cardStyle}>
        <StepperControl
          value={materialsCents > 0 ? `$${Math.round(materialsCents / 100)}` : '\u2014'}
          onDecrement={() => onChangeMaterials(Math.max(0, materialsCents - 500))}
          onIncrement={() => onChangeMaterials(materialsCents + 500)}
        />
        <PhaseAccumulator
          items={matItems}
          quantities={matQuantities}
          onIncrement={onIncrementMat}
          onDecrement={onDecrementMat}
          onClearItem={onClearMat}
          onReset={onResetMats}
          unit="$"
        />

        {isPaperBased && (
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${T.cream200}` }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              fontSize: 14, color: T.charcoal600, marginBottom: 8,
            }}>
              <span>Estimated framing</span>
              <span style={{ fontWeight: 600, color: framingWaived ? T.charcoal300 : T.charcoal800 }}>
                {framingWaived ? '$0' : `$${Math.round(framingCents / 100)}`}
              </span>
            </div>

            <label style={{
              display: 'flex', alignItems: 'center', gap: 8,
              fontSize: 13, color: T.charcoal500, cursor: 'pointer',
              marginBottom: 12,
            }}>
              <input
                type="checkbox"
                checked={framingWaived}
                onChange={(e) => onToggleFramingWaived?.(e.target.checked)}
                style={{ accentColor: T.primary, width: 16, height: 16 }}
              />
              I frame my own work
            </label>

            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              fontSize: 14, fontWeight: 600, color: T.charcoal800,
              paddingTop: 8, borderTop: `1px solid ${T.cream200}`,
            }}>
              <span>Total materials</span>
              <span>${Math.round(totalCents / 100)}</span>
            </div>
          </div>
        )}

        <StepNextButton label="Next" disabled={false} onPress={onNext} />
      </div>
    </div>
  );
}
